import type { SortModel } from '@thinkalpha/table-client';
import { z } from 'zod';

/** Per-panel view state the browser applies on top of the source table. */
export interface TableBrowserPanelView {
    /** Ordered server sort model; null or empty means the source table's own order. */
    sort: SortModel | null;
    /** Raw table filter formula; null means unfiltered. */
    filter: string | null;
}

export const EMPTY_PANEL_VIEW: TableBrowserPanelView = { sort: null, filter: null };

export function isEmptyPanelView(view: TableBrowserPanelView): boolean {
    return (view.sort === null || Object.keys(view.sort).length === 0) && (view.filter === null || view.filter === '');
}

/** One open panel as the URL describes it. */
export interface TableBrowserUrlPanel {
    sourceKey: string;
    view: TableBrowserPanelView;
}

export interface TableBrowserUrlState {
    panels: TableBrowserUrlPanel[];
    search: string;
    serverFilter: string;
}

/** Repeated per panel: a bare key for a source panel, or JSON `{k, s?, f?}` for a derived one. */
export const PANEL_PARAM = 't';
/** Legacy single-table alias written by the grid "View in Table Browser" link. */
export const LEGACY_TABLE_KEY_PARAM = 'tableKey';
export const SEARCH_PARAM = 'q';
export const SERVER_FILTER_PARAM = 'server';

const sortModelSchema = z.record(z.string(), z.enum(['ascending', 'descending']));

const encodedPanelSchema = z.object({
    k: z.string().min(1),
    s: sortModelSchema.optional(),
    f: z.string().optional(),
});

function parsePanelParam(value: string): TableBrowserUrlPanel | null {
    if (value === '') {
        return null;
    }
    if (!value.startsWith('{')) {
        return { sourceKey: value, view: EMPTY_PANEL_VIEW };
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(value);
    } catch {
        return null;
    }
    const result = encodedPanelSchema.safeParse(parsed);
    if (!result.success) {
        return null;
    }
    const { k, s, f } = result.data;
    return {
        sourceKey: k,
        view: {
            sort: s === undefined || Object.keys(s).length === 0 ? null : s,
            filter: f === undefined || f === '' ? null : f,
        },
    };
}

/** Reads the browser's state from a location search string; malformed entries are dropped. */
export function parseTableBrowserUrl(search: string): TableBrowserUrlState {
    const params = new URLSearchParams(search);
    const panels: TableBrowserUrlPanel[] = [];
    for (const value of params.getAll(PANEL_PARAM)) {
        const panel = parsePanelParam(value);
        if (panel !== null) {
            panels.push(panel);
        }
    }
    for (const value of params.getAll(LEGACY_TABLE_KEY_PARAM)) {
        if (value !== '') {
            panels.push({ sourceKey: value, view: EMPTY_PANEL_VIEW });
        }
    }
    return {
        panels,
        search: params.get(SEARCH_PARAM) ?? '',
        serverFilter: params.get(SERVER_FILTER_PARAM) ?? '',
    };
}

function serializePanel(panel: TableBrowserUrlPanel): string {
    if (isEmptyPanelView(panel.view)) {
        return panel.sourceKey;
    }
    const encoded: z.infer<typeof encodedPanelSchema> = { k: panel.sourceKey };
    if (panel.view.sort !== null && Object.keys(panel.view.sort).length > 0) {
        encoded.s = panel.view.sort;
    }
    if (panel.view.filter !== null && panel.view.filter !== '') {
        encoded.f = panel.view.filter;
    }
    return JSON.stringify(encoded);
}

/** Writes the browser's state as a search string (with the leading `?`, or empty when nothing is set). */
export function serializeTableBrowserUrl(state: TableBrowserUrlState): string {
    const params = new URLSearchParams();
    for (const panel of state.panels) {
        params.append(PANEL_PARAM, serializePanel(panel));
    }
    if (state.search !== '') {
        params.set(SEARCH_PARAM, state.search);
    }
    if (state.serverFilter !== '') {
        params.set(SERVER_FILTER_PARAM, state.serverFilter);
    }
    const serialized = params.toString();
    return serialized === '' ? '' : `?${serialized}`;
}

/** Href of the browser opened on one source table, for links from other surfaces. */
export function tableBrowserHref(sourceKey: string): string {
    return `/admin/browser${serializeTableBrowserUrl({ panels: [{ sourceKey, view: EMPTY_PANEL_VIEW }], search: '', serverFilter: '' })}`;
}
